import type { DeviceTransport,NormalizedDevice } from './types';

/** Keywords indicating bluetooth transport */
const BLUETOOTH_KEYWORDS = [
  'bluetooth',
  'bt ',
  'airpod',
  'bose',
  'jabra',
  'plantronics',
  'poly ',
  'galaxy buds',
  'wh-1000',
  'wf-1000',
  'beats',
];

/** Keywords indicating USB transport */
const USB_KEYWORDS = ['usb', 'yeti', 'snowball', 'scarlett', 'focusrite', 'blue '];

/** Keywords indicating virtual/software transport */
const VIRTUAL_KEYWORDS = ['virtual', 'vb-', 'voicemeeter', 'soundflower', 'blackhole', 'loopback'];

/** Keywords indicating builtin transport */
const BUILTIN_KEYWORDS = ['built-in', 'internal', 'integrated', 'realtek', 'macbook', 'speakers'];

/** Windows synthetic device prefixes */
const DEFAULT_PREFIX = 'Default - ';
const COMMUNICATIONS_PREFIX = 'Communications - ';

/** Transport sort priority (lower = higher priority) */
const TRANSPORT_PRIORITY: Record<DeviceTransport, number> = {
  builtin: 0,
  usb: 1,
  bluetooth: 2,
  virtual: 3,
  unknown: 4,
};

/**
 * Simple djb2 hash function for generating stable device IDs.
 * No crypto dependency needed — just needs to be consistent.
 */
export function djb2Hash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

/**
 * DeviceEnumerator normalizes raw MediaDeviceInfo[] into a canonical list
 * with stable identities, deduplication, and transport detection.
 */
export class DeviceEnumerator {
  private cachedDevices: NormalizedDevice[] = [];

  /** Enumerate audio input devices and normalize them */
  async enumerate(): Promise<NormalizedDevice[]> {
    const raw = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = raw.filter(d => d.kind === 'audioinput');
    this.cachedDevices = this.normalizeDevices(audioInputs);
    return this.cachedDevices;
  }

  /** Get the last enumerated device list without re-querying */
  getCachedDevices(): NormalizedDevice[] {
    return this.cachedDevices;
  }

  /** Find a normalized device by its browserDeviceId */
  findByBrowserDeviceId(browserDeviceId: string): NormalizedDevice | undefined {
    return this.cachedDevices.find(d => d.browserDeviceId === browserDeviceId);
  }

  /** Find a normalized device by its internalId */
  findByInternalId(internalId: string): NormalizedDevice | undefined {
    return this.cachedDevices.find(d => d.internalId === internalId);
  }

  /**
   * Pure normalization function: raw MediaDeviceInfo[] → NormalizedDevice[]
   * Exported for direct testing.
   */
  normalizeDevices(raw: MediaDeviceInfo[]): NormalizedDevice[] {
    // Step 1: Convert to intermediate representation with flags
    const intermediate = raw.map(device => {
      const isDefault = device.deviceId === 'default' || device.label.startsWith(DEFAULT_PREFIX);
      const isCommunications =
        device.deviceId === 'communications' || device.label.startsWith(COMMUNICATIONS_PREFIX);

      return {
        browserDeviceId: device.deviceId,
        rawLabel: device.label,
        displayLabel: this.cleanLabel(device.label),
        isDefault,
        isCommunications,
        transport: this.detectTransport(device.label),
        groupId: device.groupId || undefined,
        // Use raw label for hash input (before cleaning) for stability
        internalId: this.computeInternalId(device.kind, device.groupId, device.label),
      } satisfies NormalizedDevice;
    });

    // Step 2: Deduplicate Windows synthetics
    const deduped = this.deduplicateWindowsSynthetics(intermediate);

    // Step 3: Sort
    return this.sortDevices(deduped);
  }

  /** Compute a stable internal ID from device properties */
  computeInternalId(kind: string, groupId: string, rawLabel: string): string {
    // Strip synthetic prefixes before hashing so the same physical device
    // hashes identically regardless of whether we see it as "Default - X" or "X"
    const cleanedLabel = rawLabel
      .replace(DEFAULT_PREFIX, '')
      .replace(COMMUNICATIONS_PREFIX, '');
    return djb2Hash(`${kind}|${groupId ?? ''}|${cleanedLabel}`);
  }

  /** Detect the transport type from the device label */
  detectTransport(label: string): DeviceTransport {
    const lower = label.toLowerCase();

    if (BLUETOOTH_KEYWORDS.some(kw => lower.includes(kw))) return 'bluetooth';
    if (USB_KEYWORDS.some(kw => lower.includes(kw))) return 'usb';
    if (VIRTUAL_KEYWORDS.some(kw => lower.includes(kw))) return 'virtual';
    if (BUILTIN_KEYWORDS.some(kw => lower.includes(kw))) return 'builtin';

    return 'unknown';
  }

  /** Clean a device label for display */
  cleanLabel(label: string): string {
    if (!label) return 'Unknown Microphone';

    let cleaned = label;

    // Strip Windows synthetic prefixes
    if (cleaned.startsWith(DEFAULT_PREFIX)) {
      cleaned = cleaned.slice(DEFAULT_PREFIX.length);
    }
    if (cleaned.startsWith(COMMUNICATIONS_PREFIX)) {
      cleaned = cleaned.slice(COMMUNICATIONS_PREFIX.length);
    }

    // Trim whitespace and parenthetical device IDs (e.g., " (046d:0825)")
    cleaned = cleaned.replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i, '');

    return cleaned.trim() || 'Unknown Microphone';
  }

  /**
   * Deduplicate Windows synthetic "Default" and "Communications" entries.
   * These share the same groupId with the real hardware entry.
   * Merge them into the hardware entry with flags set.
   */
  deduplicateWindowsSynthetics(devices: NormalizedDevice[]): NormalizedDevice[] {
    const synthetics = devices.filter(d => d.isDefault || d.isCommunications);
    if (synthetics.length === 0) return devices;

    // Build a set of groupIds that have synthetic entries
    const syntheticGroupIds = new Set<string>();
    for (const s of synthetics) {
      if (s.groupId) syntheticGroupIds.add(s.groupId);
    }

    // For each groupId with synthetics, find the real hardware entry and merge
    const result: NormalizedDevice[] = [];
    const merged = new Set<string>(); // browserDeviceIds we've merged away

    for (const device of devices) {
      // Skip synthetic entries (default, communications) — they'll be merged into hardware
      if (
        (device.isDefault || device.isCommunications) &&
        device.groupId &&
        syntheticGroupIds.has(device.groupId)
      ) {
        // Check if there's a matching hardware entry with the same groupId
        const hasHardwareEntry = devices.some(
          d =>
            d.groupId === device.groupId &&
            !d.isDefault &&
            !d.isCommunications &&
            d.browserDeviceId !== device.browserDeviceId,
        );
        if (hasHardwareEntry) {
          merged.add(device.browserDeviceId);
          continue;
        }
      }

      // For hardware entries that have matching synthetics, set the flags
      if (
        !device.isDefault &&
        !device.isCommunications &&
        device.groupId &&
        syntheticGroupIds.has(device.groupId)
      ) {
        const hasDefault = synthetics.some(
          s => s.groupId === device.groupId && s.isDefault,
        );
        const hasComms = synthetics.some(
          s => s.groupId === device.groupId && s.isCommunications,
        );
        result.push({
          ...device,
          isDefault: hasDefault || device.isDefault,
          isCommunications: hasComms || device.isCommunications,
        });
      } else {
        result.push(device);
      }
    }

    return result;
  }

  /** Sort devices: default first, then by transport priority, then alphabetically */
  sortDevices(devices: NormalizedDevice[]): NormalizedDevice[] {
    return [...devices].sort((a, b) => {
      // Default device always first
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;

      // Then by transport priority
      const transportDiff = TRANSPORT_PRIORITY[a.transport] - TRANSPORT_PRIORITY[b.transport];
      if (transportDiff !== 0) return transportDiff;

      // Then alphabetically by display label
      return a.displayLabel.localeCompare(b.displayLabel);
    });
  }
}
